import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Button,
  Badge,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  ConfirmDialog,
} from "@/components/ui";
import { SearchableSelect } from "@/components/ui/searchable-select";
import { PageHeader } from "@/components/ui/page-header";
import { paymentsApi, type PaymentBeneficiary } from "@/services/api";
import { WIRE_COUNTRIES, SEPA_COUNTRIES } from "@/lib/countries";
import {
  Users,
  Plus,
  MoreHorizontal,
  Edit,
  Trash2,
  Loader2,
  ArrowLeft,
  X,
} from "lucide-react";
import { formatDate } from "@/lib/utils";
import { toast } from "sonner";

const railLabels: Record<string, string> = {
  wire: "Wire Transfer",
  sepa: "SEPA",
  spei: "SPEI",
};

const countryLabels: Record<string, string> = {
  US: "Estados Unidos",
  EU: "Europa",
  MX: "México",
};

interface BeneficiaryFormData {
  nickname: string;
  country: string;
  currency: string;
  paymentRail: string;
  beneficiaryName: string;
  // Wire
  routingNumber: string;
  bankAccountNumber: string;
  bankName: string;
  addressStreet: string;
  addressCity: string;
  addressState: string;
  addressPostalCode: string;
  addressCountry: string;
  // SEPA
  iban: string;
  bic: string;
  sepaCountry: string;
  sepaType: string;
  firstName: string;
  lastName: string;
  // SPEI
  speiClabe: string;
  speiBankCode: string;
}

const emptyForm: BeneficiaryFormData = {
  nickname: "",
  country: "US",
  currency: "USD",
  paymentRail: "wire",
  beneficiaryName: "",
  routingNumber: "",
  bankAccountNumber: "",
  bankName: "",
  addressStreet: "",
  addressCity: "",
  addressState: "",
  addressPostalCode: "",
  addressCountry: "",
  iban: "",
  bic: "",
  sepaCountry: "",
  sepaType: "business",
  firstName: "",
  lastName: "",
  speiClabe: "",
  speiBankCode: "",
};

const currencyRailMap: Record<string, { rail: string; country: string }> = {
  USD: { rail: "wire", country: "US" },
  EUR: { rail: "sepa", country: "EU" },
  MXN: { rail: "spei", country: "MX" },
};

export function BeneficiariosPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [formData, setFormData] = useState<BeneficiaryFormData>(emptyForm);
  const [saving, setSaving] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);

  const {
    data: beneficiaries,
    isLoading,
  } = useQuery({
    queryKey: ["payment-beneficiaries"],
    queryFn: () => paymentsApi.listBeneficiaries(),
  });

  const activeBeneficiaries = (beneficiaries || []).filter((b) => b.isActive);

  const openCreateDialog = () => {
    setEditingId(null);
    setFormData(emptyForm);
    setDialogOpen(true);
  };

  const openEditDialog = (b: PaymentBeneficiary) => {
    setEditingId(b.id);
    // Note: raw values are not available (only masked versions), so sensitive fields start empty
    setFormData({
      nickname: b.nickname,
      country: b.country,
      currency: b.currency,
      paymentRail: b.paymentRail,
      beneficiaryName: b.beneficiaryName,
      routingNumber: "",
      bankAccountNumber: "",
      bankName: b.bankName || "",
      addressStreet: "",
      addressCity: "",
      addressState: "",
      addressPostalCode: "",
      addressCountry: "",
      iban: "",
      bic: "",
      sepaCountry: "",
      sepaType: "business",
      firstName: "",
      lastName: "",
      speiClabe: "",
      speiBankCode: "",
    });
    setDialogOpen(true);
  };

  const handleCurrencyChange = (currency: string) => {
    const mapping = currencyRailMap[currency];
    if (mapping) {
      setFormData((prev) => ({
        ...prev,
        currency,
        paymentRail: mapping.rail,
        country: mapping.country,
      }));
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = {
        nickname: formData.nickname,
        country: formData.country,
        currency: formData.currency,
        paymentRail: formData.paymentRail,
        beneficiaryName: formData.beneficiaryName,
        ...(formData.paymentRail === "wire" && {
          routingNumber: formData.routingNumber || undefined,
          bankAccountNumber: formData.bankAccountNumber || undefined,
          bankName: formData.bankName || undefined,
          addressStreet: formData.addressStreet || undefined,
          addressCity: formData.addressCity || undefined,
          addressState: formData.addressState || undefined,
          addressPostalCode: formData.addressPostalCode || undefined,
          addressCountry: formData.addressCountry || undefined,
        }),
        ...(formData.paymentRail === "sepa" && {
          iban: formData.iban || undefined,
          bic: formData.bic || undefined,
          sepaCountry: formData.sepaCountry || undefined,
          sepaType: formData.sepaType || undefined,
          firstName: formData.firstName || undefined,
          lastName: formData.lastName || undefined,
        }),
        ...(formData.paymentRail === "spei" && {
          speiClabe: formData.speiClabe || undefined,
          speiBankCode: formData.speiBankCode || undefined,
        }),
      };

      if (editingId) {
        await paymentsApi.updateBeneficiary(editingId, payload);
        toast.success("Beneficiário atualizado", {
          style: { background: "#495565", color: "#ffffff" },
        });
      } else {
        await paymentsApi.createBeneficiary(payload);
        toast.success("Beneficiário cadastrado", {
          style: { background: "#495565", color: "#ffffff" },
        });
      }

      setDialogOpen(false);
      queryClient.invalidateQueries({ queryKey: ["payment-beneficiaries"] });
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : "Erro desconhecido";
      toast.error(editingId ? "Erro ao atualizar" : "Erro ao cadastrar", {
        description: message,
        style: { background: "#495565", color: "#ffffff" },
      });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deletingId) return;
    setDeleting(true);
    try {
      await paymentsApi.deleteBeneficiary(deletingId);
      toast.success("Beneficiário removido", {
        style: { background: "#495565", color: "#ffffff" },
      });
      setDeleteDialogOpen(false);
      setDeletingId(null);
      queryClient.invalidateQueries({ queryKey: ["payment-beneficiaries"] });
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : "Erro desconhecido";
      toast.error("Erro ao remover", {
        description: message,
        style: { background: "#495565", color: "#ffffff" },
      });
    } finally {
      setDeleting(false);
    }
  };

  const canSave =
    formData.nickname &&
    formData.beneficiaryName &&
    formData.currency &&
    (formData.paymentRail === "wire"
      ? formData.routingNumber && formData.bankAccountNumber
      : formData.paymentRail === "sepa"
      ? formData.iban
      : formData.paymentRail === "spei"
      ? formData.speiClabe
      : false);

  return (
    <div className="p-4 sm:p-6 space-y-4 sm:space-y-6">
      <PageHeader
        icon={<Users className="h-5 w-5 sm:h-6 sm:w-6" />}
        title="Beneficiários"
        description="Gerencie seus beneficiários para pagamentos internacionais"
        actions={
          <div className="flex gap-2">
            <Button
              className="bg-white/20 hover:bg-white/30 text-white border-0"
              onClick={() => navigate("/pagamentos")}
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Pagamentos
            </Button>
            <Button
              className="bg-white/20 hover:bg-white/30 text-white border-0"
              onClick={openCreateDialog}
            >
              <Plus className="w-4 h-4 mr-2" />
              Novo Beneficiário
            </Button>
          </div>
        }
      />

      {/* Table */}
      <Card>
        <CardContent className="p-0">
          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
            </div>
          ) : activeBeneficiaries.length === 0 ? (
            <div className="p-8 text-center">
              <Users className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
              <p className="text-muted-foreground mb-4">
                Nenhum beneficiário cadastrado
              </p>
              <Button onClick={openCreateDialog}>
                <Plus className="h-4 w-4 mr-2" />
                Cadastrar Primeiro Beneficiário
              </Button>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="border-b border-border bg-muted/30">
                  <tr className="text-left text-sm text-muted-foreground">
                    <th className="p-4 font-medium">Apelido</th>
                    <th className="p-4 font-medium hidden md:table-cell">País</th>
                    <th className="p-4 font-medium">Moeda</th>
                    <th className="p-4 font-medium hidden sm:table-cell">Rail</th>
                    <th className="p-4 font-medium">Beneficiário</th>
                    <th className="p-4 font-medium hidden lg:table-cell">Cadastrado</th>
                    <th className="p-4 font-medium text-right">Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {activeBeneficiaries.map((b) => (
                    <tr
                      key={b.id}
                      className="border-b border-border last:border-0 hover:bg-muted/20"
                    >
                      <td className="p-4">
                        <span className="font-medium">{b.nickname}</span>
                      </td>
                      <td className="p-4 hidden md:table-cell">
                        <span className="text-sm">
                          {countryLabels[b.country] || b.country}
                        </span>
                      </td>
                      <td className="p-4">
                        <Badge variant="secondary">{b.currency}</Badge>
                      </td>
                      <td className="p-4 hidden sm:table-cell">
                        <span className="text-sm">
                          {railLabels[b.paymentRail] || b.paymentRail}
                        </span>
                      </td>
                      <td className="p-4">
                        <span className="text-sm">{b.beneficiaryName}</span>
                      </td>
                      <td className="p-4 hidden lg:table-cell">
                        <span className="text-sm text-muted-foreground">
                          {formatDate(b.createdAt)}
                        </span>
                      </td>
                      <td className="p-4 text-right">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={(e) => e.stopPropagation()}
                            >
                              <MoreHorizontal className="w-4 h-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => openEditDialog(b)}>
                              <Edit className="w-4 h-4 mr-2" />
                              Editar
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem
                              className="text-red-600"
                              onClick={() => {
                                setDeletingId(b.id);
                                setDeleteDialogOpen(true);
                              }}
                            >
                              <Trash2 className="w-4 h-4 mr-2" />
                              Remover
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Create/Edit Card Overlay Modal */}
      {dialogOpen && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-2 sm:p-4">
          <Card className="w-full max-w-2xl max-h-[90vh] flex flex-col">
            <CardHeader className="flex flex-row items-center justify-between pb-4 flex-shrink-0">
              <CardTitle className="text-lg">
                {editingId ? "Editar Beneficiário" : "Novo Beneficiário"}
              </CardTitle>
              <Button variant="ghost" size="sm" onClick={() => setDialogOpen(false)}>
                <X className="w-4 h-4" />
              </Button>
            </CardHeader>
            <CardContent className="space-y-4 overflow-y-auto flex-1 pb-4">
              {/* === SECTION: Dados Básicos === */}
              <div className="space-y-4">
                <p className="text-sm font-medium text-muted-foreground border-b pb-2">Dados Básicos</p>

                {/* Apelido */}
                <div>
                  <Label>Apelido *</Label>
                  <Input
                    placeholder="Ex: Fornecedor ABC - USD"
                    maxLength={100}
                    value={formData.nickname}
                    onChange={(e) =>
                      setFormData((prev) => ({ ...prev, nickname: e.target.value }))
                    }
                  />
                </div>

                {/* Moeda/Corredor */}
                <div>
                  <Label>Moeda / Rota de Pagamento *</Label>
                  <Select
                    disabled={!!editingId}
                    value={formData.currency}
                    onValueChange={handleCurrencyChange}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="USD">USD - Wire Transfer</SelectItem>
                      <SelectItem value="EUR">EUR - SEPA</SelectItem>
                      <SelectItem value="MXN">MXN - SPEI</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Nome do Beneficiário */}
                <div>
                  <Label>Nome do Beneficiário *</Label>
                  <Input
                    maxLength={200}
                    value={formData.beneficiaryName}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        beneficiaryName: e.target.value,
                      }))
                    }
                  />
                </div>
              </div>

              {/* === SECTION: Dados Bancários === */}
              <div className="space-y-4">
                <p className="text-sm font-medium text-muted-foreground border-b pb-2">Dados Bancários</p>

                {/* Wire fields */}
                {formData.paymentRail === "wire" && (
                  <>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label>Routing Number *</Label>
                        <Input
                          placeholder="021000089"
                          maxLength={9}
                          value={formData.routingNumber}
                          onChange={(e) =>
                            setFormData((prev) => ({
                              ...prev,
                              routingNumber: e.target.value.replace(/\D/g, ""),
                            }))
                          }
                          error={formData.routingNumber && formData.routingNumber.length !== 9 ? "Deve ter 9 dígitos" : undefined}
                        />
                        <p className="text-xs text-muted-foreground mt-1">9 dígitos — encontrado no cheque ou site do banco</p>
                      </div>
                      <div>
                        <Label>Número da Conta *</Label>
                        <Input
                          maxLength={17}
                          value={formData.bankAccountNumber}
                          onChange={(e) =>
                            setFormData((prev) => ({
                              ...prev,
                              bankAccountNumber: e.target.value.replace(/\D/g, ""),
                            }))
                          }
                          error={formData.bankAccountNumber && (formData.bankAccountNumber.length < 4 || formData.bankAccountNumber.length > 17) ? "Entre 4 e 17 dígitos" : undefined}
                        />
                      </div>
                    </div>
                    <div>
                      <Label>Nome do Banco</Label>
                      <Input
                        value={formData.bankName}
                        onChange={(e) =>
                          setFormData((prev) => ({
                            ...prev,
                            bankName: e.target.value,
                          }))
                        }
                      />
                    </div>
                  </>
                )}

                {/* SEPA fields */}
                {formData.paymentRail === "sepa" && (
                  <>
                    <div>
                      <Label>Tipo</Label>
                      <Select
                        value={formData.sepaType}
                        onValueChange={(v) =>
                          setFormData((prev) => ({ ...prev, sepaType: v }))
                        }
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="business">Empresa</SelectItem>
                          <SelectItem value="individual">Pessoa Física</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    {formData.sepaType === "individual" && (
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label>Nome</Label>
                          <Input
                            value={formData.firstName}
                            onChange={(e) =>
                              setFormData((prev) => ({
                                ...prev,
                                firstName: e.target.value,
                              }))
                            }
                          />
                        </div>
                        <div>
                          <Label>Sobrenome</Label>
                          <Input
                            value={formData.lastName}
                            onChange={(e) =>
                              setFormData((prev) => ({
                                ...prev,
                                lastName: e.target.value,
                              }))
                            }
                          />
                        </div>
                      </div>
                    )}
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label>IBAN *</Label>
                        <Input
                          placeholder="DE89 3704 0044 0532 0130 00"
                          maxLength={34}
                          value={formData.iban}
                          onChange={(e) =>
                            setFormData((prev) => ({
                              ...prev,
                              iban: e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, ""),
                            }))
                          }
                          error={formData.iban && (formData.iban.length < 15 || formData.iban.length > 34) ? "IBAN deve ter entre 15 e 34 caracteres" : undefined}
                        />
                        <p className="text-xs text-muted-foreground mt-1">Código internacional da conta bancária</p>
                      </div>
                      <div>
                        <Label>BIC / SWIFT</Label>
                        <Input
                          placeholder="COBADEFFXXX"
                          maxLength={11}
                          value={formData.bic}
                          onChange={(e) =>
                            setFormData((prev) => ({
                              ...prev,
                              bic: e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, ""),
                            }))
                          }
                          error={formData.bic && formData.bic.length !== 8 && formData.bic.length !== 11 ? "Deve ter 8 ou 11 caracteres" : undefined}
                        />
                      </div>
                    </div>
                    <div>
                      <Label>País *</Label>
                      <SearchableSelect
                        options={SEPA_COUNTRIES.map((c) => ({ value: c.code, label: `${c.code} - ${c.name}` }))}
                        value={formData.sepaCountry}
                        onChange={(v) => setFormData((prev) => ({ ...prev, sepaCountry: v }))}
                        placeholder="Selecione o país"
                        searchPlaceholder="Buscar país..."
                      />
                    </div>
                  </>
                )}

                {/* SPEI fields */}
                {formData.paymentRail === "spei" && (
                  <>
                    <div>
                      <Label>CLABE *</Label>
                      <Input
                        placeholder="012345678901234567"
                        maxLength={18}
                        value={formData.speiClabe}
                        onChange={(e) =>
                          setFormData((prev) => ({
                            ...prev,
                            speiClabe: e.target.value.replace(/\D/g, ""),
                          }))
                        }
                        error={formData.speiClabe && formData.speiClabe.length !== 18 ? `${formData.speiClabe.length}/18 dígitos` : undefined}
                      />
                      <p className="text-xs text-muted-foreground mt-1">18 dígitos — Clave Bancaria Estandarizada</p>
                    </div>
                    <div>
                      <Label>Código do Banco</Label>
                      <Input
                        placeholder="012"
                        maxLength={3}
                        value={formData.speiBankCode}
                        onChange={(e) =>
                          setFormData((prev) => ({
                            ...prev,
                            speiBankCode: e.target.value.replace(/\D/g, ""),
                          }))
                        }
                      />
                    </div>
                  </>
                )}
              </div>

              {/* === SECTION: Endereço (only for wire) === */}
              {formData.paymentRail === "wire" && (
                <div className="space-y-4">
                  <p className="text-sm font-medium text-muted-foreground border-b pb-2">Endereço</p>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Rua</Label>
                      <Input
                        value={formData.addressStreet}
                        onChange={(e) =>
                          setFormData((prev) => ({
                            ...prev,
                            addressStreet: e.target.value,
                          }))
                        }
                      />
                    </div>
                    <div>
                      <Label>Cidade</Label>
                      <Input
                        value={formData.addressCity}
                        onChange={(e) =>
                          setFormData((prev) => ({
                            ...prev,
                            addressCity: e.target.value,
                          }))
                        }
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <Label>Estado</Label>
                      <Input
                        placeholder="NY"
                        maxLength={2}
                        value={formData.addressState}
                        onChange={(e) =>
                          setFormData((prev) => ({
                            ...prev,
                            addressState: e.target.value.toUpperCase(),
                          }))
                        }
                      />
                    </div>
                    <div>
                      <Label>ZIP Code</Label>
                      <Input
                        placeholder="10001"
                        maxLength={10}
                        value={formData.addressPostalCode}
                        onChange={(e) =>
                          setFormData((prev) => ({
                            ...prev,
                            addressPostalCode: e.target.value.replace(/[^0-9-]/g, ""),
                          }))
                        }
                      />
                    </div>
                    <div>
                      <Label>País *</Label>
                      <SearchableSelect
                        options={WIRE_COUNTRIES.map((c) => ({ value: c.code, label: `${c.code} - ${c.name}` }))}
                        value={formData.addressCountry}
                        onChange={(v) => setFormData((prev) => ({ ...prev, addressCountry: v }))}
                        placeholder="Selecione o país"
                        searchPlaceholder="Buscar país..."
                      />
                    </div>
                  </div>
                </div>
              )}
            </CardContent>

            {/* Fixed footer */}
            <div className="flex justify-end gap-3 p-4 border-t flex-shrink-0">
              <Button variant="outline" onClick={() => setDialogOpen(false)} disabled={saving}>
                Cancelar
              </Button>
              <Button onClick={handleSave} disabled={!canSave || saving}>
                {saving ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Salvando...
                  </>
                ) : editingId ? (
                  "Salvar Alterações"
                ) : (
                  "Cadastrar"
                )}
              </Button>
            </div>
          </Card>
        </div>
      )}

      {/* Delete dialog */}
      <ConfirmDialog
        open={deleteDialogOpen}
        onClose={() => {
          setDeleteDialogOpen(false);
          setDeletingId(null);
        }}
        title="Remover Beneficiário"
        description="Tem certeza que deseja remover este beneficiário? Ele não poderá ser usado em novos pagamentos."
        confirmText="Remover"
        variant="danger"
        onConfirm={handleDelete}
        loading={deleting}
      />
    </div>
  );
}
